// Libraries
import React from 'react';

// Supermove
import {gql} from '@supermove/graphql';
import {useNavigationDOM, useResponsive, useTrackButton, PopoverType} from '@supermove/hooks';
import {Location, LocationModel} from '@supermove/models';
import {colors} from '@supermove/styles';
import {List} from '@supermove/utils';

// Shared
import ActionMenuPopover from '@shared/design/components/ActionMenu/ActionMenuPopover';
import ViewLocationButtons from '@shared/modules/Location/components/ViewLocationButtons';

const LocationActionsPopover = ({
  location,
  popover,
  handleEdit,
  handleDelete,
  isStop,
  isRemovable = true,
  children,
}: {
  location: LocationModel;
  popover: PopoverType;
  handleEdit: () => void;
  handleDelete: () => void;
  isStop?: boolean;
  isRemovable?: boolean;
  children: React.ReactNode;
}) => {
  const responsive = useResponsive();
  const {navigator} = useNavigationDOM();

  const editActions = [
    {
      text: `Edit ${isStop ? 'stop' : 'location'}`,
      onPress: handleEdit,
    },
    ...List.insertIf(isRemovable, {
      text: `${isStop ? 'Remove stop' : 'Delete location'}`,
      onPress: handleDelete,
      color: colors.red.warning,
    }),
  ];
  const trackGoogleMapsButton = useTrackButton({name: 'Google Maps Button'});
  const trackZillowButton = useTrackButton({name: 'Zillow Button'});
  const trackStreetViewButton = useTrackButton({name: 'Street View Button'});

  return (
    <ActionMenuPopover
      popover={popover}
      placement={ActionMenuPopover.Position.BottomEnd}
      actions={
        responsive.desktop
          ? editActions
          : [
              {
                label: 'Open',
                actions: ViewLocationButtons.getMenuActions({
                  navigator,
                  location,
                  trackGoogleMapsButton,
                  trackZillowButton,
                  trackStreetViewButton,
                }),
              },
              {
                label: 'Edit',
                actions: editActions,
              },
            ]
      }
      width={140}
    >
      {children}
    </ActionMenuPopover>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
LocationActionsPopover.fragment = gql`
  ${Location.getZillowLink.fragment}
  ${Location.getStreetViewLink.fragment}
  ${ViewLocationButtons.fragment}

  fragment LocationActionsPopover on Location {
    id
    address
    ...Location_getZillowLink
    ...Location_getStreetViewLink
    ...ViewLocationButtons
  }
`;

export default LocationActionsPopover;
